/* eslint-disable @typescript-eslint/no-duplicate-enum-values */
import { IconType } from '@aibees/shared/components';
import { EnrichmentMode, ParsedPreset } from '@aibees/shared/definition';
import { UseBoolean } from '@aibees/shared/hooks';
import { JSX, ReactNode } from 'react';
import { FieldValues } from 'react-hook-form';

export interface TemplateManagementDialogProps {
  className?: string;
  open: UseBoolean;
  element?: Required<JSX.Element>;
  onClose: () => void;
  onBack?: () => void;
  dialogTitle?: string;
  dialogIcon?: IconType;
  defaultItems: TemplateColumns;
  dataType: TemplateType | string;
  mode?: OperationMode;
  templateName?: string;
  selectedTemplate?: ParsedPreset<any> | null;
  error?: ReactNode;
  checkedValues?: FieldValues;
  setSelectedTemplate?: (template: ParsedPreset<any> | null) => void;
  integrationConfig?: Record<string, any>;
}

export enum CategoryType {
  COMPANY = 'COMPANY',
  PEOPLE = 'PEOPLE',
  EMAIL = 'EMAIL',
}

export enum TemplateType {
  COMPANY = 'COMPANY',
  PERSON = 'PERSON',
  AI_PERSONALITY = 'AI_PERSONALITY',
}

export enum OperationMode {
  EDIT = 'EDIT',
  CREATE = 'CREATE',
}

export enum CategoryKey {
  basic = 'basic',
  email = 'email',
  company = 'company',
  experiences = 'experiences',
  educations = 'educations',
  awards = 'awards',
  courses = 'courses',
  projects = 'projects',
  languages = 'languages',
  certifications = 'certifications',
  volunteerExperiences = 'volunteerExperiences',
  publications = 'publications',
  companyLocations = 'companyLocations',
  patents = 'patents',
}

export interface TemplateInformation {
  key: string;
  title?: string;
  description?: string;
  icon: IconType;
}
export interface TemplateColumns {
  items: TemplateCategory[];
  information?: TemplateInformation;
  categoryWithNoChildren?: boolean;
}

export interface TemplateCategory {
  key: CategoryKey;
  name: string;
  type: CategoryType;
  isSingleEntry?: boolean;
  disabled?: boolean;
  hasInfoBadge?: boolean;
  entries: TemplateEntry[];
}

export interface TemplateEntry {
  name: string;
  count?: number;
  checked: boolean;
  notAdded?: boolean;
  isBottom?: boolean;
  fields: TemplateOption[];
  index?: number;
}

export interface TemplateOption {
  name: string;
  description?: string;
  icon?: IconType;
  selected?: boolean;
  disabled?: boolean;
  comingSoon?: boolean;
  hasInfoBadge?: boolean;
  type: OptionType;
  hasPeopleAi?: boolean;
  hidden?: boolean;
}

export interface NewStateEntry {
  index: number;
  isBottom?: boolean;
  notAdded?: boolean;
  fields: OptionType[];
}

export interface TransferredData {
  key: CategoryKey;
  categoryName: string;
  disabled?: boolean;
  isSingleEntry?: boolean;
  entries: NewStateEntry[];
}

export interface EditData {
  id: string;
  name: string;
  type: EnrichmentMode;
  value: TransferredData[];
}
export enum OptionType {
  FIRSTNAME = 'FIRSTNAME',
  LASTNAME = 'LASTNAME',
  FULL_NAME = 'FULL_NAME',
  HEADLINE = 'HEADLINE',
  PROFILE_PICTURE = 'PROFILE_PICTURE',
  DATE_OF_BIRTH = 'DATE_OF_BIRTH',
  INFLUENCER = 'INFLUENCER',
  PREMIUM = 'PREMIUM',
  OPEN_TO_WORK = 'OPEN_TO_WORK',
  CREATOR = 'CREATOR',
  EMAIL_BUSINESS = 'EMAIL_BUSINESS',
  BUSINESS_STATUS = 'BUSINESS_STATUS',
  MX_RECORD = 'MX_RECORD',
  DOMAIN_SETTING = 'DOMAIN_SETTING',
  MOBILE_PHONE = 'MOBILE_PHONE',
  WORK_PHONE = 'WORK_PHONE',
  HOME_PHONE = 'HOME_PHONE',
  DEPARTMENT = 'DEPARTMENT',
  SENIORITY = 'SENIORITY',
  ORG = 'ORG',
  JOB_COUNT = 'JOB_COUNT',
  SEARCH_INPUT = 'SEARCH_INPUT',
  RELEVANCY = 'RELEVANCY',
  COMPANY_URL = 'COMPANY_URL',
  COMPANY = 'COMPANY',
  COMPANY_STREET = 'COMPANY_STREET',
  LOCALITY = 'LOCALITY',
  IS_CURRENT = 'IS_CURRENT',
  GRADE = 'GRADE',
  DEGREE = 'DEGREE',
  SCHOOL = 'SCHOOL',
  FIELD = 'FIELD',
  NUMBER = 'NUMBER',
  LANGUAGE = 'LANGUAGE',
  PROFICIENCY = 'PROFICIENCY',
  AUTHORITY = 'AUTHORITY',
  LICENSE_NUMBER = 'LICENSE_NUMBER',
  DISPLAY_SOURCE = 'DISPLAY_SOURCE',
  COMPANY_ID = 'COMPANY_ID',
  COMPANY_DESCRIPTION = 'COMPANY_DESCRIPTION',
  COMPANY_LOGO = 'COMPANY_LOGO',
  START_DATE = 'START_DATE',
  END_DATE = 'END_DATE',
  ROLE = 'ROLE',
  COMPANY_NAME = 'COMPANY_NAME',
  COMPANY_TYPE = 'COMPANY_TYPE',
  COMPANY_LINKEDIN_URL = 'COMPANY_LINKEDIN_URL',
  PUBLISHER = 'PUBLISHER',
  URL = 'URL',
  TITLE = 'TITLE',
  ISSUER = 'ISSUER',
  SUMMARY = 'SUMMARY',
  PATENT_NUMBER = 'PATENT_NUMBER',
  APPLICATION_NUMBER = 'APPLICATION_NUMBER',
  DATE = 'DATE',
  NAME = 'NAME',
  DESCRIPTION = 'DESCRIPTION',
  SEO_DESCRIPTION = 'SEO_DESCRIPTION',
  TYPE = 'TYPE',
  INDUSTRY = 'INDUSTRY',
  FOUNDING_YEAR = 'FOUNDING_YEAR',
  LOGO = 'LOGO',
  EMPLOYEE_COUNT = 'EMPLOYEE_COUNT',
  SIZE = 'SIZE',
  WEBSITE = 'WEBSITE',
  DOMAIN = 'DOMAIN',
  LINKEDIN = 'LINKEDIN',
  TWITTER = 'TWITTER',
  FACEBOOK = 'FACEBOOK',
  INSTAGRAM = 'INSTAGRAM',
  LOCATION = 'LOCATION',
  COUNTRY = 'COUNTRY',
  STATE = 'STATE',
  CITY = 'CITY',
  POSTAL_CODE = 'POSTAL_CODE',
  ECONOMIC_ZONE = 'ECONOMIC_ZONE',
  NUMBER_OF_LOCATION = 'NUMBER_OF_LOCATION',
  PRIMARY_PHONE = 'PRIMARY_PHONE',
  SECONDARY_PHONE = 'SECONDARY_PHONE',
  ANNUAL_REVENUE = 'ANNUAL_REVENUE',
  LAST_FUNDING_TYPE = 'LAST_FUNDING_TYPE',
  TOTAL_FUNDING = 'TOTAL_FUNDING',
  TOTAL_FUNDING_AMOUNT = 'TOTAL_FUNDING_AMOUNT',
  LAST_FUNDING_AMOUNT = 'LAST_FUNDING_AMOUNT',
  LAST_FUNDING_DATE = 'LAST_FUNDING_DATE',
  LAST_FUNDING_EVENT = 'LAST_FUNDING_EVENT',
  LISTS = 'LISTS',
  LABELS = 'LABELS',
  PRODUCT_AND_SERVICES = 'PRODUCT_AND_SERVICES',
  OP_LANGUAGE = 'OP_LANGUAGE',
  TECHNOLOGIES = 'TECHNOLOGIES',
  ID = 'ID',
  ORG_ID = 'ORG_ID',
  SLUG = 'SLUG',
  OWNER = 'OWNER',
  FOLLOWER_COUNT = 'FOLLOWER_COUNT',
  ADDRESS = 'ADDRESS',
  PRIMARY = 'PRIMARY',
  SPECIALITIES = 'SPECIALITIES',
  COMPANY_DOMAIN = 'COMPANY_DOMAIN',
  COMPANY_ADDRESS = 'COMPANY_ADDRESS',
  HEADCOUNT = 'HEADCOUNT',
}
